import {Edge, Node} from 'reactflow';

import {ProcessFlowchartNodeType} from 'processes/graph-builder-processes/flowchart-viz/ProcessFlowchartVizConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

/**
 * Generic interfaces for flowchart visualization
 */
export const FlowchartDragDropKey = 'application/graphbuilder';

export enum FlowchartNodeShape {
  RECTANGLE = 'RECTANGLE',
  OVAL = 'OVAL',
  DIAMOND = 'DIAMOND',
  PILL = 'PILL',
  CIRCLE = 'CIRCLE',
}

export interface NodeStyles {
  shapeStyle: object;
  errorStyle: object | null;
  actionFunctionStyle: object | null;
  nameStyle: object | null;
}

export interface FlowchartNodeDefinition {
  nodeStyles: NodeStyles;
  dropKey: string;
}

export interface FlowchartNodeInstanceData {
  shape: FlowchartNodeShape;
  height: number;
  width: number;
  // The height of a node name placed above the node, if applicable
  nameHeight?: number;
}

export interface FlowchartNodeInfo {
  nodeDefinition: FlowchartNodeDefinition;
  instanceData: FlowchartNodeInstanceData;
}

/**
 * Generic node and edge interfaces
 */

export interface FlowchartBaseNode {
  name: string;
  clientId: string;
  isHighlighted?: boolean;
  isInaccessible?: boolean; // The schema, and/or the corresponding task group, is inaccessible
  type: string;
  layout?: {
    position: {x: number; y: number};
  };
  onClick?: () => void;
  instanceData: FlowchartNodeInstanceData;
}

interface EdgePoints {
  x: number;
  y: number;
}

export interface EdgeLayoutData {
  points: Array<EdgePoints>;
  labelWidth?: number;
  labelHeight?: number;
  labelX?: number;
  labelY?: number;
}

export interface FlowchartBaseEdge {
  clientId: string;
  fromNodeClientId: string;
  toNodeClientId: string;
  layout?: EdgeLayoutData;
}

export interface FlowchartStaticNode extends FlowchartBaseNode {}

export interface FlowchartStaticEdge extends FlowchartBaseEdge {}

export interface FlowchartInteractiveNode extends FlowchartBaseNode {
  locked?: boolean;
}

export interface ComputedEdgeLayoutData {
  edgePath: string;
  labelX: number;
  labelY: number;
  hoverAreaWidth: number;
  hoverAreaHeight: number;
  hoverAreaX: number;
  hoverAreaY: number;
}

export interface EdgeIdToComputedEdgeData {
  [key: string]: ComputedEdgeLayoutData;
}

export interface FlowchartInteractiveEdge extends FlowchartBaseEdge {
  locked?: boolean;
}

export type FlowchartActionItem = FlowchartBaseNode | FlowchartBaseEdge;

export interface FlowchartActionFunctionOnInvokeParams<
  TItem extends FlowchartActionItem,
  TFlowchart extends FlowchartBase,
> {
  item: TItem;
  flowchart: TFlowchart;
  updateHandler: (flowchart: TFlowchart) => void;
}

export interface FlowchartActionFunction<
  TItem extends FlowchartActionItem,
  TFlowchart extends FlowchartBase,
> {
  icon: BenchlingIconName;
  name: string;
  tooltipText: string;
  flowchart: TFlowchart;
  updateHandler: (flowchart: TFlowchart) => void;
  onInvoke: (params: FlowchartActionFunctionOnInvokeParams<TItem, TFlowchart>) => void;
  getIsDisabled?: (item: TItem, flowchart: TFlowchart) => boolean;
}

// We separate the actionFunctions from the FlowchartBaseNode and FlowchartBaseEdge interfaces because the
// FlowchartActionFunction contains a reference to the Flowchart itself. So if we included action functions
// on the node and edge themselves we create recursive data structures which can cause infinite recursive
// loops when they are modified and processed.
export type WithFlowchartActionFunctions<T extends FlowchartActionItem> = T & {
  actionFunctions?: Array<FlowchartActionFunction<T, FlowchartBase>>;
};

export interface FlowchartMetadata {
  title?: string;
}

export interface Flowchart<TNode, TEdge> {
  clientId: string;
  nodes: Array<TNode>;
  edges: Array<TEdge>;
  metadata?: FlowchartMetadata;
}
export type FlowchartBase = Flowchart<FlowchartBaseNode, FlowchartBaseEdge>;

export type NodeWithData<T> = Node<T> & {data: T};
export type EdgeWithData<T> = Edge<T> & {data: T};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface FlowchartRenderData<TNode = any, TEdge = any> {
  nodes: Array<NodeWithData<TNode>>;
  edges: Array<EdgeWithData<TEdge>>;
  // This bounds the position (point) data. The rendered nodes will of course extend beyond these boundaries.
  centersBoundingBox: {top: number; bottom: number};
}

export const NODE_TYPES_WITH_TARGET_HANDLE = [
  ProcessFlowchartNodeType.END_NODE,
  ProcessFlowchartNodeType.MIDDLE_NODE,
];

export const NODE_TYPES_WITH_SOURCE_HANDLE = [
  ProcessFlowchartNodeType.START_NODE,
  ProcessFlowchartNodeType.MIDDLE_NODE,
];
